import { SlugPicker } from './SlugPicker';
import type { SlugDef } from '../../lib/broadcastSlugs';
import { useTextInsertion } from './useTextInsertion';

// Slug-aware text field for the subject line (single) and the SMS body
// (multiline + character counter). No markdown toolbar — SMS is plain text and
// subjects don't render markdown.

type SlugTextFieldProps = {
  value: string;
  onChange: (next: string) => void;
  multiline?: boolean;
  rows?: number;
  placeholder?: string;
  // Show an SMS-style character + segment counter beneath the field.
  smsCounter?: boolean;
  // Slug catalogue (defaults to broadcast SLUG_DEFS); surveys pass SURVEY_SLUG_DEFS.
  defs?: SlugDef[];
};

// GSM segmenting: a single SMS holds 160 chars; longer messages split into
// 153-char concatenated parts. Unicode bodies are smaller, but this estimate is
// the common case and good enough for a compose-time hint.
function smsSegments(length: number): number {
  if (length === 0) return 0;
  if (length <= 160) return 1;
  return Math.ceil(length / 153);
}

export function SlugTextField({
  value,
  onChange,
  multiline = false,
  rows = 6,
  placeholder,
  smsCounter = false,
  defs,
}: SlugTextFieldProps) {
  const input = useTextInsertion<HTMLInputElement>(value, onChange);
  const textarea = useTextInsertion<HTMLTextAreaElement>(value, onChange);
  const insertAtCursor = multiline ? textarea.insertAtCursor : input.insertAtCursor;

  return (
    <div>
      <div className="flex items-start gap-2">
        {multiline ? (
          <textarea
            ref={textarea.ref}
            value={value}
            onChange={(e) => onChange(e.target.value)}
            rows={rows}
            placeholder={placeholder}
            className="w-full rounded border border-slate-300 px-3 py-2 text-sm"
          />
        ) : (
          <input
            ref={input.ref}
            type="text"
            value={value}
            onChange={(e) => onChange(e.target.value)}
            placeholder={placeholder}
            className="w-full rounded border border-slate-300 px-3 py-2 text-sm"
          />
        )}
        <SlugPicker onInsert={insertAtCursor} defs={defs} />
      </div>
      {smsCounter && (
        <p className="mt-1 text-xs text-slate-500">
          {value.length} characters · {smsSegments(value.length)} SMS segment
          {smsSegments(value.length) === 1 ? '' : 's'}
        </p>
      )}
    </div>
  );
}
